import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { endpoints, withSideload } from '@rossum/api-client';
import { AnnotationsSearchQuery } from '@rossum/api-client/annotations';
import { GridFilterItem } from '@rossum/ui/x-data-grid-pro';
import { FETCH_INTERVAL } from '../../../constants/values';
import { api } from '../../../lib/apiClient';
import {
  dashboardRefreshIntervalSelector,
  organizationGroupSelector,
} from '../../../redux/modules/organizationGroup/selectors';
import { LevelOptions } from '../../document-list-base/types';
import { DISALLOWED_STATUSES_FILTER_ITEM } from '../../document-list-columns/status/statuses';
import { MetaColDef } from '../../document-list-columns/TypedGridColDef';
import { DashboardAnnotationListWithSideload } from '../../document-list-columns/utils';
import { detailsEncoder } from '../../mql/detailsCodec';
import { encodeMqlQuery } from '../../mql/encodeMqlQuery';
import { labelsFeatureSelector } from '../../pricing/selectors';
import { resolveUserSideloadsByColumn, transformResponse } from '../helpers';
import { AllDocsAnnotation } from '../types';

export const ALL_DOCUMENTS_QUERY_KEY = 'all-documents';

const DEFAULT_ORDERING = '-created_at';

/** Faster poll used while any row is still being reindexed. */
const OUTDATED_FETCH_INTERVAL = 3000;

export const useFetchDashboardData = ({
  query,
  searchQuery,
  dataGridFiltering,
  isTableConfigLoading,
  statusColumns,
}: {
  statusColumns: MetaColDef<AllDocsAnnotation>[];
  isTableConfigLoading: boolean;
  query: AnnotationsSearchQuery;
  searchQuery: string | undefined;
  dataGridFiltering: GridFilterItem[];
  level: LevelOptions | Omit<string, LevelOptions> | undefined;
}) => {
  const orgGroupLoaded = !!useSelector(organizationGroupSelector);

  const refreshInterval =
    useSelector(dashboardRefreshIntervalSelector) ?? FETCH_INTERVAL;

  const userSideloadConfig = resolveUserSideloadsByColumn({
    columnNames: statusColumns.filter(c => c.visible).map(c => c.metaName),
  });

  const resolvedQuery = {
    ...query,
    ordering: query.ordering || DEFAULT_ORDERING,
  };

  const labelsEnabled = useSelector(labelsFeatureSelector);

  const mqlQuery = encodeMqlQuery(
    [...dataGridFiltering, DISALLOWED_STATUSES_FILTER_ITEM],
    {
      columnEncoders: {
        details: detailsEncoder,
      },
    }
  );

  return useQuery(
    [
      ALL_DOCUMENTS_QUERY_KEY,
      resolvedQuery,
      mqlQuery,
      searchQuery,
      userSideloadConfig,
    ] as const,
    (): Promise<DashboardAnnotationListWithSideload> =>
      api.request(
        withSideload(
          endpoints.annotations.search(resolvedQuery, {
            query: mqlQuery,
            ...(searchQuery && { queryString: { string: searchQuery } }),
          }),
          {
            workflowRuns: true,
            workflowSteps: true,
            relations: true,
            documents: true,
            childRelations: true,
            labels: labelsEnabled || undefined,
            ...userSideloadConfig,
          }
        )
      ),
    {
      enabled: orgGroupLoaded && !isTableConfigLoading,
      refetchInterval: (data, { state: queryState }) => {
        if (queryState.error) return false;

        return data?.results.some(result => result.isOutdated)
          ? Math.min(OUTDATED_FETCH_INTERVAL, refreshInterval)
          : refreshInterval;
      },
      retry: (_, error) => !error,
      select: useCallback(
        (response: DashboardAnnotationListWithSideload) => {
          const filteredStatuses: Array<string> =
            dataGridFiltering?.find(
              i => i.field === 'status' && i.operator === 'isAnyOf'
            )?.value || [];

          const filteredQueues: Array<number> = (
            dataGridFiltering?.find(
              i => i.field === 'queue' && i.operator === 'isAnyOf'
            )?.value || []
          ).flatMap((value: string) => parseInt(value, 10) || []);

          const queueSet = new Set(filteredQueues);
          const statusSet = new Set(filteredStatuses);

          return transformResponse(response, statusSet, queueSet);
        },
        [dataGridFiltering]
      ),
    }
  );
};

export const useSetDashboardData = () => {
  const queryClient = useQueryClient();

  const setDashboardData = (
    callback: (
      response: DashboardAnnotationListWithSideload | undefined
    ) => DashboardAnnotationListWithSideload | undefined
  ) =>
    queryClient.setQueriesData<DashboardAnnotationListWithSideload | undefined>(
      [ALL_DOCUMENTS_QUERY_KEY],
      response => callback(response)
    );

  return setDashboardData;
};

export const useInvalidateDashboardData = () => {
  const queryClient = useQueryClient();

  return useCallback(
    () => queryClient.invalidateQueries([ALL_DOCUMENTS_QUERY_KEY]),
    [queryClient]
  );
};
